import { useEffect, useRef, useState } from "react";

/**
 * useLiveBotEvents — subscribes to /api/ws/bots and exposes per-bot animation
 * state (flash direction + trade bump counts) so any UI can light up on every
 * trade open/close within ~1s of execution.
 *
 * Usage:
 *   const { connected, flashes, tradeBumps, events } = useLiveBotEvents({
 *     onEvent: (ev) => {
 *       // optimistic local update of your bots array
 *       setBots(curr => curr.map(b => b.id === ev.bot_id ? {...b, ...} : b));
 *     },
 *   });
 *
 *   <span className={flashes[bot.id] === "up" ? "ring-2 ring-green-400" : ""}>
 *     {bot.total_pnl_pct}%{tradeBumps[bot.id] ? ` +${tradeBumps[bot.id]}` : ""}
 *   </span>
 */
export function useLiveBotEvents({ onEvent } = {}) {
  const [connected, setConnected] = useState(false);
  const [flashes, setFlashes] = useState({});
  const [tradeBumps, setTradeBumps] = useState({});
  const [heartbeats, setHeartbeats] = useState({}); // {bot_id: timestamp_ms}
  const [events, setEvents] = useState([]); // newest first, cap 20
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    const apiBase = process.env.REACT_APP_BACKEND_URL || "";
    if (!apiBase) return undefined;
    const wsUrl =
      apiBase.replace(/^http/, "ws").replace(/\/$/, "") + "/api/ws/bots";

    let ws;
    let reconnectTimer;
    let attempts = 0;
    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      try {
        ws = new WebSocket(wsUrl);
      } catch {
        scheduleReconnect();
        return;
      }
      ws.onopen = () => {
        attempts = 0;
        setConnected(true);
      };
      ws.onclose = () => {
        setConnected(false);
        scheduleReconnect();
      };
      ws.onerror = () => {
        // onclose follows
      };
      ws.onmessage = (msg) => {
        let ev;
        try {
          ev = JSON.parse(msg.data);
        } catch {
          return;
        }
        if (!ev || ev.type === "hello") return;
        if (!ev.bot_id) return;

        // Heartbeat: bot was ticked (no trade necessarily) — pulse row + update
        // displayed P&L/trade count from the fresh post-tick numbers.
        if (ev.type === "heartbeat") {
          setHeartbeats((curr) => ({ ...curr, [ev.bot_id]: Date.now() }));
          if (onEventRef.current) {
            try {
              onEventRef.current(ev);
            } catch (_e) {
              // ignore consumer errors
            }
          }
          setTimeout(() => {
            setHeartbeats((curr) => {
              const { [ev.bot_id]: _drop, ...rest } = curr;
              return rest;
            });
          }, 1500);
          return;
        }

        if (ev.type !== "open" && ev.type !== "close") return;

        const direction =
          (ev.pnl_stxoshi || 0) >= 0 || (ev.pnl_pct || 0) >= 0 ? "up" : "down";

        setEvents((curr) => [ev, ...curr].slice(0, 20));
        setFlashes((curr) => ({ ...curr, [ev.bot_id]: direction }));
        setTradeBumps((curr) => ({
          ...curr,
          [ev.bot_id]: (curr[ev.bot_id] || 0) + 1,
        }));

        setTimeout(() => {
          setFlashes((curr) => {
            const { [ev.bot_id]: _drop, ...rest } = curr;
            return rest;
          });
        }, 1800);
        setTimeout(() => {
          setTradeBumps((curr) => {
            const { [ev.bot_id]: _drop, ...rest } = curr;
            return rest;
          });
        }, 4000);

        if (onEventRef.current) {
          try {
            onEventRef.current(ev);
          } catch (_e) {
            // never let consumer errors kill the socket loop
          }
        }
      };
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      attempts += 1;
      const delay = Math.min(15_000, 1000 * 2 ** Math.min(4, attempts));
      reconnectTimer = setTimeout(connect, delay);
    };

    connect();
    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws && ws.readyState <= 1) {
        try {
          ws.close();
        } catch (_e) {
          // ignore
        }
      }
    };
  }, []);

  return { connected, flashes, tradeBumps, heartbeats, events };
}
